import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@xstyled/styled-components';
import { useEditorStore } from '~/shell/Store';
import { t } from 'ttag';
import { useWebSocketChat } from '~/training/sequence/templates/items/chatBot/hooks/useWebSocketChat';
import { ChatBotService } from '~/training/sequence/templates/items/chatBot/services/chatBotService';
import { useTrainingStore } from '~/training/state';
import { LearnerTrainingStatus } from '~/data';
import { ChatHeader } from '~/training/sequence/templates/items/chatBot/components/chat/ChatHeader';
import { ChatMessageList } from '~/training/sequence/templates/items/chatBot/components/chat/ChatMessageList';
import { MessageComposer } from '~/training/sequence/templates/items/chatBot/components/chat/MessageComposer';

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: calc(100vh - 150px);
`;

const DisclaimerText = styled.p`
  font-size: 11px;
  line-height: 1.5;
  color: #6f6f6f;
  padding: 0px 80px 18px;
  text-align: center;
  flex: 0 0 auto;

  @media (max-width: 768px) {
    font-size: 9px;
    padding: 0px 14px 18px;
  }
`;

export const ChatInterface = ({
  chatBotId,
  shouldSendInitialMessage = false,
  picture,
  title
}: {
  chatBotId?: string;
  shouldSendInitialMessage?: boolean;
  picture?: {
    id: string;
    name: string;
    mediaUrl: string;
  };
  title?: string;
}) => {
  const [message, setMessage] = useState('');
  const [hasStartedChat, setHasStartedChat] = useState(false);
  const chatContainerRef = useRef<HTMLElement | null>(null);

  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, []);

  const { contentEditable } = useEditorStore();
  const status = useTrainingStore(state => state.status);

  const isSequenceFinished = status === LearnerTrainingStatus.Completed;

  const [chatBotService, setChatBotService] = useState<ChatBotService | null>(
    null
  );

  useEffect(() => {
    if (contentEditable) return;

    const learnerTrainingId = useTrainingStore.getState().learnerTrainingId;
    const chapterSequenceId = useTrainingStore.getState().chapterSequenceId;
    const service = new ChatBotService({
      chatBotId,
      learnerTrainingId,
      chapterSequenceId,
      conversationName: title
    });
    setChatBotService(service);
  }, [contentEditable, chatBotId]);

  const { messages, loading, sendMessage, sendInitialMessage } =
    useWebSocketChat({
      chatBotService: contentEditable ? null : chatBotService,
      disableAutoConversationCreation: shouldSendInitialMessage
    });

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (
      !contentEditable &&
      chatBotService &&
      shouldSendInitialMessage &&
      !hasStartedChat
    ) {
      sendInitialMessage();
      setHasStartedChat(true);
    }
  }, [
    contentEditable,
    chatBotService,
    shouldSendInitialMessage,
    sendInitialMessage,
    hasStartedChat
  ]);

  const handleSendMessage = useCallback(
    async (text: string) => {
      if (!text || text.trim().length < 1 || isSequenceFinished) return;

      setTimeout(scrollToBottom, 0);
      try {
        setMessage('');
        await sendMessage(text);
        scrollToBottom();
      } catch (e) {
        setMessage(text);
        console.error('Failed to send message:', e);
      }
    },
    [sendMessage, isSequenceFinished, scrollToBottom]
  );

  const handleVoiceTranscript = useCallback(
    (transcript: string) => {
      if (!isSequenceFinished) setMessage(transcript);
    },
    [isSequenceFinished]
  );

  const CHAT_BOT_TITLE = title || 'Lima Ai';

  return (
    <ChatContainer className="chatMessageRoot">
      <ChatHeader logoSrc={picture?.mediaUrl} title={CHAT_BOT_TITLE} />
      <ChatMessageList
        ref={chatContainerRef}
        messages={messages || []}
        loading={loading}
        botTitle={CHAT_BOT_TITLE}
      />
      <MessageComposer
        message={message}
        onMessageChange={setMessage}
        onSendMessage={handleSendMessage}
        onVoiceTranscript={handleVoiceTranscript}
        loading={loading}
        disabled={isSequenceFinished}
        placeholder={
          isSequenceFinished ? t`Chat completed` : t`Type your message...`
        }
      />
      <DisclaimerText>
        {t`This practice conversation is generated by AI to support your learning through practice. While the experience is designed to be helpful and generated dynamically, it may not cover every nuance and is not a substitute for your organization’s official policies. If you have any questions about responses, check with your employer.`}
      </DisclaimerText>
    </ChatContainer>
  );
};
